<template>
	<div
		class="tooltip-wrapper"
		@mouseenter="showTooltip"
		@mouseleave="hideTooltip"
		@focus="showTooltip"
		@blur="hideTooltip"
	>
		<!-- Слот для элемента, к которому привязан tooltip -->
		<slot></slot>

		<!-- Tooltip -->
		<transition name="tooltip-fade">
			<div
				v-if="isVisible"
				ref="tooltipRef"
				class="tooltip"
				:class="[`tooltip-${computedPosition}`, customClass]"
				role="tooltip"
			>
				<div class="tooltip-content">
					{{ text }}
				</div>
				<div class="tooltip-arrow"></div>
			</div>
		</transition>
	</div>
</template>

<script>
import { ref, computed } from 'vue'

export default {
	name: 'Tooltip',
	props: {
		// Текст подсказки
		text: {
			type: String,
			required: true,
		},
		// Позиция: 'top', 'bottom', 'left', 'right', 'auto'
		position: {
			type: String,
			default: 'top',
			validator: (value) =>
				['top', 'bottom', 'left', 'right', 'auto'].includes(value),
		},
		// Задержка перед показом (мс)
		delay: {
			type: Number,
			default: 200,
		},
		// Дополнительный CSS класс
		customClass: {
			type: String,
			default: '',
		},
	},
	setup(props) {
		const isVisible = ref(false)
		const tooltipRef = ref(null)
		let timeoutId = null

		// Вычисляемая позиция (для auto определяем лучшую позицию)
		const computedPosition = computed(() => {
			if (props.position !== 'auto') {
				return props.position
			}
			// Для auto по умолчанию используем top
			// В реальном проекте здесь можно добавить логику определения позиции
			// на основе положения элемента относительно viewport
			return 'top'
		})

		const showTooltip = () => {
			// Проверка на prefers-reduced-motion
			const prefersReducedMotion = window.matchMedia(
				'(prefers-reduced-motion: reduce)'
			).matches

			if (prefersReducedMotion) {
				// Показываем без задержки для пользователей с ограниченными возможностями
				isVisible.value = true
			} else {
				timeoutId = setTimeout(() => {
					isVisible.value = true
				}, props.delay)
			}
		}

		const hideTooltip = () => {
			if (timeoutId) {
				clearTimeout(timeoutId)
				timeoutId = null
			}
			isVisible.value = false
		}

		return {
			isVisible,
			tooltipRef,
			computedPosition,
			showTooltip,
			hideTooltip,
		}
	},
}
</script>

<style scoped>
/* Wrapper */
.tooltip-wrapper {
	position: relative;
	display: inline-block;
}

/* Tooltip */
.tooltip {
	position: absolute;
	z-index: 10000;
	padding: 8px 12px;
	background: rgba(45, 45, 45, 0.95);
	color: white;
	font-family: var(--font-family-poppins, 'Poppins', sans-serif);
	font-size: 13px;
	font-weight: 400;
	line-height: 1.4;
	border-radius: 6px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	white-space: nowrap;
	pointer-events: none;
	backdrop-filter: blur(10px);
}

.tooltip-content {
	position: relative;
	z-index: 1;
}

/* Стрелка */
.tooltip-arrow {
	position: absolute;
	width: 0;
	height: 0;
	border-style: solid;
}

/* Позиции */
.tooltip-top {
	bottom: calc(100% + 8px);
	left: 50%;
	transform: translateX(-50%);
}

.tooltip-top .tooltip-arrow {
	top: 100%;
	left: 50%;
	transform: translateX(-50%);
	border-width: 6px 6px 0 6px;
	border-color: rgba(45, 45, 45, 0.95) transparent transparent transparent;
}

.tooltip-bottom {
	top: calc(100% + 8px);
	left: 50%;
	transform: translateX(-50%);
}

.tooltip-bottom .tooltip-arrow {
	bottom: 100%;
	left: 50%;
	transform: translateX(-50%);
	border-width: 0 6px 6px 6px;
	border-color: transparent transparent rgba(45, 45, 45, 0.95) transparent;
}

.tooltip-left {
	right: calc(100% + 8px);
	top: 50%;
	transform: translateY(-50%);
}

.tooltip-left .tooltip-arrow {
	left: 100%;
	top: 50%;
	transform: translateY(-50%);
	border-width: 6px 0 6px 6px;
	border-color: transparent transparent transparent rgba(45, 45, 45, 0.95);
}

.tooltip-right {
	left: calc(100% + 8px);
	top: 50%;
	transform: translateY(-50%);
}

.tooltip-right .tooltip-arrow {
	right: 100%;
	top: 50%;
	transform: translateY(-50%);
	border-width: 6px 6px 6px 0;
	border-color: transparent rgba(45, 45, 45, 0.95) transparent transparent;
}

/* Анимация появления */
.tooltip-fade-enter-active {
	transition: opacity 0.2s ease, transform 0.2s ease;
}

.tooltip-fade-leave-active {
	transition: opacity 0.15s ease, transform 0.15s ease;
}

.tooltip-fade-enter-from,
.tooltip-fade-leave-to {
	opacity: 0;
}

.tooltip-top.tooltip-fade-enter-from,
.tooltip-top.tooltip-fade-leave-to {
	transform: translateX(-50%) translateY(4px);
}

.tooltip-bottom.tooltip-fade-enter-from,
.tooltip-bottom.tooltip-fade-leave-to {
	transform: translateX(-50%) translateY(-4px);
}

.tooltip-left.tooltip-fade-enter-from,
.tooltip-left.tooltip-fade-leave-to {
	transform: translateY(-50%) translateX(4px);
}

.tooltip-right.tooltip-fade-enter-from,
.tooltip-right.tooltip-fade-leave-to {
	transform: translateY(-50%) translateX(-4px);
}

/* Адаптивность */
@media (max-width: 768px) {
	.tooltip {
		font-size: 12px;
		padding: 6px 10px;
	}
}

/* Поддержка prefers-reduced-motion */
@media (prefers-reduced-motion: reduce) {
	.tooltip-fade-enter-active,
	.tooltip-fade-leave-active {
		transition: none;
	}
}
</style>

